import { Action } from "redux";
import { produce, Draft, isDraft, isDraftable } from "immer";
import {
	loop,
	Cmd,
	Loop,
	CmdType,
	isLoop,
	RunCmd,
	ActionCmd,
	ListCmd,
	MapCmd,
	SetIntervalCmd,
	SetTimeoutCmd,
} from "redux-loop";
import { RemoteReduxAction } from "./types";

/**
 * Use `unImmer()` to safely pass objects from reducers to effects.
 *
 * @param obj Any object
 * @returns any
 */
export const unImmer = (obj: any) => JSON.parse(JSON.stringify(obj));

type DeepReadonly<T> = T extends (infer R)[]
	? DeepReadonlyArray<R>
	: T extends Function
		? T
		: T extends object
			? DeepReadonlyObject<T>
			: T;

interface DeepReadonlyArray<T> extends ReadonlyArray<DeepReadonly<T>> {}

type DeepReadonlyObject<T> = {
	readonly [P in keyof T]: DeepReadonly<T[P]>;
};

export interface AnyAction extends Action {
	// Allows any extra properties to be defined in an action.
	[extraProps: string]: any;
}
export interface TypedActionCreator<Type extends string> {
	(...args: any[]): Action<Type>;
	type: Type;
}

// The original
//type MyCaseReducer<S = any, A extends Action = AnyAction> = (state: Draft<S>, action: A) => S | void | Draft<S>;

// Automatically run produce
//type MyCaseReducer<S = any, A extends Action = AnyAction> = (state: Draft<S>, action: A) => void | Loop<S|Draft<S>>;

// Run produce manually
type MyCaseReducer<S = any, A extends Action = AnyAction> = (
	state: DeepReadonly<S>,
	action: A
) => DeepReadonly<S> | Loop<DeepReadonly<S>>;

// Reducers that use immer return void. Those that don't use immer must return the state.
type LoopCaseReducer<S = any, A extends Action = AnyAction> = (
	state: Draft<S>,
	action: A & RemoteReduxAction,
	cmd: CMD
) => void | S;

export interface CMD {
	run: typeof Cmd.run;
	action: typeof Cmd.action;
	list: typeof Cmd.list;
	map: typeof Cmd.map;
	setTimeout: typeof Cmd.setTimeout;
	setInterval: typeof Cmd.setInterval;
	clearTimeout: typeof Cmd.clearTimeout;
	clearInterval: typeof Cmd.clearInterval;
}

const formatRunCmd = (cmd: RunCmd) => {
	let obj: any = {
		RUN: cmd.func.name,
	};
	if (cmd.args && cmd.args.length > 0) {
		// nested if args contains an object
		if (cmd.args.some((arg) => typeof arg === "object")) obj.args = cmd.args;
		// string, for quick reading
		else obj.args = cmd.args.join(" ");
	}
	if (cmd.successActionCreator) obj.successActionCreator = cmd.successActionCreator.name;

	if (cmd.failActionCreator) obj.successActionCreator = cmd.failActionCreator.name;

	return obj;
};

const formatActionCmd = (cmd: ActionCmd<any>) => {
	const { type, ...members } = cmd.actionToDispatch;
	return {
		ACTION: type,
		...members,
	};
};

/**
 * Formats redux-loop cmds for very quick human perusal in redux-devtools.
 */
const formatCmdForDisplay = (cmd) => {
	switch (cmd.type) {
		case "RUN": {
			return formatRunCmd(cmd);
		}
		case "LIST":
			return {
				...cmd,
				cmds: cmd.cmds.map(formatCmdForDisplay),
			};
		case "MAP":
			return {
				...cmd,
				tagger: cmd.tagger.name,
				nestedCmd: formatCmdForDisplay(cmd.nestedCmd),
			};
		case "ACTION":
			return formatActionCmd(cmd);
		case "NONE":
		default:
			return cmd;
	}
};

/**
 * Intercepts calls to `cmd()` within reducers. The cmds are remembered and then
 * consolidated into a singl Cmd.List() at the end of the reducer process (bottom of this file).
 */
class CmdProxy implements CMD {
	commands: any[] = [];

	constructor() {}

	run(fn: (...args: any[]) => Promise<any> | any, args: any): RunCmd<never, never> {
		let runCmd = Cmd.run(fn, args);
		this.commands.push(runCmd);
		return runCmd;
	}

	action<A extends Action<any>>(action: A): ActionCmd<A> {
		let runCmd = Cmd.action(action);
		this.commands.push(runCmd);
		return runCmd;
	}

	list(cmds: CmdType[], options?: any): ListCmd {
		let runCmd = Cmd.list(cmds, options);
		this.commands.push(runCmd);
		return runCmd;
	}

	map<A extends Action, B extends Action>(cmd: CmdType, tagger: (subAction: B) => A, args?: any[]): MapCmd<A> {
		let runCmd = Cmd.map(cmd, tagger, args);
		this.commands.push(runCmd);
		return runCmd;
	}

	clearTimeout(timerId: number): RunCmd {
		let runCmd = Cmd.clearTimeout(timerId);
		this.commands.push(runCmd);
		return runCmd;
	}

	clearInterval(timerId: number): RunCmd {
		let runCmd = Cmd.clearInterval(timerId);
		this.commands.push(runCmd);
		return runCmd;
	}

	setTimeout<A extends Action = never>(
		cmd: CmdType,
		delayMs: number,
		options?: {
			scheduledActionCreator?: (timerId: number) => A;
		}
	): SetTimeoutCmd<A> {
		let runCmd = Cmd.setTimeout(cmd, delayMs, options);
		this.commands.push(runCmd);
		return runCmd;
	}

	setInterval<A extends Action = never>(
		cmd: CmdType,
		delayMs: number,
		options?: {
			scheduledActionCreator?: (timerId: number) => A;
		}
	): SetIntervalCmd<A> {
		let runCmd = Cmd.setInterval(cmd, delayMs, options);
		this.commands.push(runCmd);
		return runCmd;
	}
}

export interface ActionReducerMapBuilder<State> {
	addNoImmerCase<ActionCreator extends TypedActionCreator<string>>(
		actionCreator: ActionCreator,
		reducer: MyCaseReducer<State, ReturnType<ActionCreator>>
	): ActionReducerMapBuilder<State>;
	addNoImmerCase<Type extends string, A extends Action<Type>>(
		type: Type,
		reducer: MyCaseReducer<State, A>
	): ActionReducerMapBuilder<State>;
	addCase<ActionCreator extends TypedActionCreator<string>>(
		actionCreator: ActionCreator,
		reducer: LoopCaseReducer<State, ReturnType<ActionCreator>>
	): ActionReducerMapBuilder<State>;
	addCase<Type extends string, A extends Action<Type>>(
		type: Type,
		reducer: LoopCaseReducer<State, A>
	): ActionReducerMapBuilder<State>;
}

type CaseReducerValue<State> = {
	reducer?: MyCaseReducer<State>;
	loopReducer?: LoopCaseReducer<State>;
};

type CaseReducers<State> = {
	[key: string]: CaseReducerValue<State>[];
};

/**
 * A slimmed down version of RTK's builder. See comments below in createReducer() for more information.
 */
export function executeReducerBuilderCallback<S>(
	builderCallback: (builder: ActionReducerMapBuilder<S>) => void
): CaseReducers<S> {
	// Unlike RTK, supports multiple reducer cases per action, but does not support isMatcher or default cases
	const actionsMap: CaseReducers<S> = {};

	const builder: ActionReducerMapBuilder<S> = {
		addNoImmerCase(typeOrActionCreator: string | TypedActionCreator<any>, reducer: MyCaseReducer<S>) {
			const type = typeof typeOrActionCreator === "string" ? typeOrActionCreator : typeOrActionCreator.type;
			actionsMap[type] = (actionsMap[type] ?? []).concat({ reducer });
			return builder;
		},

		addCase(typeOrActionCreator: string | TypedActionCreator<any>, loopReducer: LoopCaseReducer<S>) {
			const type = typeof typeOrActionCreator === "string" ? typeOrActionCreator : typeOrActionCreator.type;
			actionsMap[type] = (actionsMap[type] ?? []).concat({ loopReducer });
			return builder;
		},
	};
	builderCallback(builder);
	return actionsMap;
}

export type Reducer<S = any, A extends Action = AnyAction> = (state: S | undefined, action: A) => S;

export type CreateReducerHook = <S>(previousState: S, nextState: S, action: AnyAction) => void;

/**
 * Runs the loop reducer on a non-draftable reducer (one that returns a value rather than using immer). This
 * has a little extra logic to protect against the reducer being used improperly.
 */
const reduceNonDraftable = <S>(
	loopReducer: LoopCaseReducer<S, AnyAction>,
	state: S,
	action: AnyAction,
	cmdProxy: CmdProxy
) => {
	const resultState = loopReducer(state as any, action as AnyAction & RemoteReduxAction, cmdProxy);
	// case reducers that aren't working on immer drafts should always return a value
	// The sole exception is the first pass through (indicated by previousState being null)
	if (typeof resultState === "undefined") {
		if (state !== null) {
			throw Error("A case reducer on a non-draftable value must not return undefined");
		}
		return state;
	}
	return resultState;
};

/**
 * This is what makes the redux-toolkit style createReducer function compatible with redux-loops.
 * It runs the reducer (loopReducer from redux-loop) but instead of running each individual Cmd, it compiles
 * them into an array so that they can all be run in one swoop.
 */
const runLoopReducer = <S>(
	caseReducers: CaseReducerValue<S>[],
	state: S,
	action: AnyAction,
	hook?: CreateReducerHook
) => {
	let commands: any[] = [];
	const reducedState = caseReducers.reduce((previousState: S, caseReducer: CaseReducerValue<S>): S => {
		const cmdProxy = new CmdProxy();

		let nextState: S = previousState;
		if (caseReducer) {
			const { reducer, loopReducer } = caseReducer;
			if (reducer) {
				nextState = reducer(previousState as DeepReadonly<S>, action) as S;
				if (isLoop(nextState)) {
					commands = commands.concat([nextState[1]]);
					nextState = nextState[0];
				}
			} else if (loopReducer) {
				if (isDraft(previousState)) {
					// If it's already a draft, we must already be inside a `createNextState` call,
					// likely because this is being wrapped in `createReducer`, `createSlice`, or nested
					// inside an existing draft. It's safe to just pass the draft to the mutator. We
					// run loopReducer here only to get the commands
					loopReducer(previousState as Draft<S>, action as AnyAction & RemoteReduxAction, cmdProxy);
					commands = commands.concat(cmdProxy.commands);
				} else if (!isDraftable(previousState)) {
					// If state is not draftable (ex: a primitive, such as 0), we want to directly
					// return the caseReducer func and not wrap it with produce.
					nextState = reduceNonDraftable<S>(loopReducer, previousState, action, cmdProxy);
				} else {
					nextState = produce(previousState, (draft: Draft<S>) => {
						loopReducer(draft, action as AnyAction & RemoteReduxAction, cmdProxy);
						commands = commands.concat(cmdProxy.commands);
					});
				}
			}
		}

		hook?.(previousState, nextState, action);

		return nextState;
	}, state);
	return { reducedState, commands };
};
/**
 * This requires that redux-loops middleware has been installed in the store.
 *
 * This is a forked version of the custom createReducer() from React-Toolkit (RTK).
 * This version adds support for redux-loops by passing a `cmd` as an optional third parameter in the reducer call.
 * That `cmd` is a proxy for redux-loop's "Cmd" and can be use in the same way.
 *
 * Unlike RTK's createReducer, this version does allow you to add multiple reducer cases for the same dispatch. Be
 * careful not to make assumptions about which reducer will run first! This is best used when there are no dependencies.
 *
 * The following RTK functionality has been removed: addMatcher(), addDefaultCase(), "Map Object" notation
 *
 * Direct state mutation via return values is still supported but a special builder function `addNoImmerCase`
 * is better suited for those situations because it enforces read only state. This case will also not send a redux-loop
 * cmd as a third parameter because you can use redux-loops the normal way.
 *
 * Example: Using redux-loop
 * ```
 * builder.addCase(myAction, (state, action: PayloadAction<ClientAdded>, cmd) => {
 * 		state.blah = action.payload.blah; // Do normal reducer stuff
 *
 * 		// Create a loop, see redux-loop documentation
 * 		cmd.run(getLinkerState, {
 * 			args : [windowName],
 * 			successActionCreator : actions.initializeWindow
 * 		})
 * })
 * ```
 *
 * Use `Cmd.getState`, `Cmd.dispatch` or `Cmd.none` by importing them from redux-loop. There aren't equivalents for cmd because
 * redux-loop uses the "unique symbol" type which cannot be copied (or so it seems).
 *
 * Example: Using Cmd.getState
 * ```
 * cmd.run(getLinkerState, { args: [Cmd.getState] })
 * ```
 *
 * Example: using addNoImmerCase
 * ```
 * builder.addNoImmerCase(myAction, (state, action: PayloadAction<ClientAdded>) => {
 * 		const newState = produce(state, (draft) => {
 * 			draft.blah=action.payload.blah; // Good!
 * 			state.blah=action.payload.blah; // Typescript error, you can't modify state!
 * 		});
 *
 * 		// Use redux-loop the old fashioned way
 * 		return loop(
 * 			newState,
 * 			Cmd.action(actions.dummy("blah"))
 * 		)
 * });
 *
 * ```
 *
 * Internals:
 *
 * Like RTK, the reducer cases are reduced to single nextState and returned to redux. To extract the loops
 * we use the CmdProxy to temporarily store the loop commands. They are compiled into a single `Cmd.list`
 * at the very end of the dispatch cycle. So in effect, redux is always receiving a loop from this reducer
 * rather than a state. This will therefore not work correctly if redux-loops middleware is not installed!
 */
export function createReducer<S>(
	initialState: S,
	mapOrBuilderCallback: (builder: ActionReducerMapBuilder<S>) => void,
	hook?: CreateReducerHook
): Reducer<S> {
	const actionsMap = executeReducerBuilderCallback(mapOrBuilderCallback);

	return function (state: S = initialState, action): S {
		const caseReducers = actionsMap[action.type];
		if (!caseReducers?.length) {
			return state;
		}

		const { reducedState, commands } = runLoopReducer<S>(caseReducers, state, action, hook);

		const cmds = Cmd.list(commands);

		/**
		 * If any cmds are to be run, inject them into state so that we can see them using devtools.
		 * We jump through some hoops here in order to format the result for the quickest observation.
		 * The convoluted code is worth the result.
		 */
		// Scrub out the old @@cmds
		const { "@@cmds": oldCmds, ...scrubbedState } = reducedState as any;
		const oneOrNoneCommandState =
			cmds.cmds.length == 1
				? {
						...scrubbedState,
						"@@cmds": formatCmdForDisplay(cmds.cmds[0]),
					}
				: scrubbedState;
		// Add in the new @@cmds, if any
		const loopEnhancedState =
			cmds.cmds.length > 1
				? {
						...scrubbedState,
						"@@cmds": cmds.cmds.map((cmd) => formatCmdForDisplay(cmd)),
					}
				: oneOrNoneCommandState;

		// Hard casting to avoid the store thinking it is a `Loop<S> | S` type
		// This is okay because redux-loop middleware extracts the loops inside of redux
		return loop(loopEnhancedState, cmds) as any as S;
	};
}

/**
 * Use this instead of console.log() when inside of reducers. Since addCase uses
 * immer under the covers, console.log will print out the entire immer proxy object which is ugly
 */
export const log = (label: any, value: any) => {
	if (value) console.log(label, JSON.parse(JSON.stringify(value)));
	else console.log(label, value);
};

/**
 * Predicate function for comparing an actionCreator to a raw action
 * Example: `addMatcherCase(is(action.joinChannels), ...`
 */
export const is =
	(actionCreator: any): ((action: AnyAction) => boolean) =>
	(action: AnyAction) =>
		action.type === actionCreator.type;
